 
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Combobox, Field } from '@/design-system';
import { countryLabel, searchCountries } from '@/shared/lib/countries';
import { institutionLabel, searchInstitutions, type FinancialInstitution } from '@/shared/lib/institutions';
import { countriesQuery } from '@/shared/lookups';

interface BaseProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: ReactNode;
  help?: ReactNode;
  optional?: boolean;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

function useErrorText(error: { message?: string } | undefined): string | undefined {
  const { t } = useTranslation();
  if (!error?.message) return undefined;
  return t(error.message as 'form.required', { defaultValue: error.message });
}

/**
 * Country or nationality from the ISO 3166-1 dataset; stores the alpha-2 code. Search by country name or nationality
 * in English or Arabic, by code or by a common alias (UAE, KSA, UK). A legacy stored value that is not a code is shown
 * as stored until the user picks a country.
 */
export function CountryField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className, placeholder, as = 'country' }: BaseProps<T> & { as?: 'country' | 'nationality' }) {
  const { field, fieldState } = useController({ control, name });
  const { t, i18n } = useTranslation();
  const error = useErrorText(fieldState.error);
  const countries = useQuery(countriesQuery);
  const [q, setQ] = useState('');
  const list = countries.data;
  const options = useMemo(
    () =>
      searchCountries(q, list).map((c) => {
        const primary = countryLabel(c.code, { locale: i18n.language, as, list: list ?? [c] });
        const secondary = as === 'nationality' ? countryLabel(c.code, { locale: i18n.language, as: 'country', list: list ?? [c] }) : undefined;
        return { value: c.code, label: primary, description: secondary ? `${secondary} · ${c.code}` : c.code };
      }),
    [q, list, i18n.language, as],
  );
  const value = (field.value as string | null | undefined) ?? null;
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Combobox
        value={value || null}
        onChange={(v) => {
          // Cleared: keep the form's own "empty" (null for nullable fields, '' otherwise).
          field.onChange(v ?? (field.value === null ? null : ''));
          field.onBlur();
        }}
        options={options}
        onSearchChange={setQ}
        placeholder={placeholder ?? t('form.selectPlaceholder')}
        searchPlaceholder={t(as === 'nationality' ? 'reference.searchNationality' : 'reference.searchCountry')}
        selectedLabel={value ? countryLabel(value, { locale: i18n.language, as, ...(list ? { list } : {}) }) : undefined}
        disabled={disabled}
      />
    </Field>
  );
}

/**
 * A bank or finance company of the bank master (Lookups.financialInstitutions), searchable by code, name, Arabic
 * name and aliases. Stores the institution's id, or its code with `valueKey="code"`. Inactive institutions are not
 * offered (C3), except the value already selected.
 */
export function InstitutionField<T extends FieldValues>({ control, name, label, help, optional, required, disabled, className, placeholder, institutions, valueKey = 'id', exclude }: BaseProps<T> & { institutions: readonly FinancialInstitution[]; valueKey?: 'id' | 'code'; exclude?: readonly string[] }) {
  const { field, fieldState } = useController({ control, name });
  const { t, i18n } = useTranslation();
  const error = useErrorText(fieldState.error);
  const [q, setQ] = useState('');
  const value = (field.value as string | null | undefined) ?? null;
  const options = useMemo(
    () =>
      searchInstitutions(q, institutions)
        // C3: inactive institutions (not on the CBUAE register) are not offered for a new value; the value on file stays.
        .filter((i) => (i.active || i[valueKey] === value) && !exclude?.includes(i[valueKey]))
        .map((i) => ({ value: i[valueKey], label: institutionLabel(i, i18n.language), ...(i.aliases.length ? { description: i.aliases.slice(0, 3).join(', ') } : {}) })),
    [q, institutions, exclude, valueKey, i18n.language, value],
  );
  const selected = value ? institutions.find((i) => i[valueKey] === value) : undefined;
  return (
    <Field label={label} help={help} optional={optional} required={required} error={error} className={className}>
      <Combobox
        value={value || null}
        onChange={(v) => {
          // Cleared: keep the form's own "empty" (null for nullable fields, '' otherwise).
          field.onChange(v ?? (field.value === null ? null : ''));
          field.onBlur();
        }}
        options={options}
        onSearchChange={setQ}
        placeholder={placeholder ?? t('form.selectPlaceholder')}
        searchPlaceholder={t('reference.searchInstitution')}
        selectedLabel={selected ? institutionLabel(selected, i18n.language) : undefined}
        disabled={disabled}
      />
    </Field>
  );
}
